/**
 * WHO-5947. Transport seam for Pro Buyer reservations ("on hold" internally).
 *
 * WHY NOT APOLLO
 * Same reason as `src/features/pro-buyer-price/data/pro-buyer-price-client.ts`:
 * codegen validates every `gql` template and `.graphql` document against a
 * live gateway schema, and the testing gateway behind next.whoppah.com lags
 * production. One document naming `Product.reservationEligibility` would fail
 * codegen and take down the Vercel builds. So the operations are plain
 * strings (no `gql` tag) and every reader is fail-soft: a gateway without the
 * fields, an error, a timeout or the admin flag being off all come back as
 * "no reservation data", and nothing renders.
 *
 * Leakage rule (decision 13): these selections never ask for anything about
 * the other party. The gateway types carry nothing about them either.
 *
 * Once the gateway fields are on production, move these to `.graphql`
 * documents and delete this file.
 */
import { GRAPHQL_ENDPOINT } from "@/config";

export type ReservationStatus =
  | "REQUESTED"
  | "ACCEPTED"
  | "DECLINED"
  | "REQUEST_EXPIRED"
  | "EXPIRED"
  | "RELEASED"
  | "LIFTED"
  | "COMPLETED";

export type ReservationIneligibleReason =
  | "NOT_LOGGED_IN"
  | "NOT_PRO_BUYER"
  | "OWN_LISTING"
  | "NOT_RESERVABLE"
  | "RESERVED"
  | "ALREADY_REQUESTED"
  | "LIMIT_REACHED"
  | "COOLDOWN"
  | "FEATURE_DISABLED";

export type ReservationMoney = { amount: number; currency: string };

/** `BuyerReservation` from the contract. Nothing about the seller. */
export type BuyerReservation = {
  id: string;
  status: ReservationStatus;
  requestExpiresAt: string;
  reservedUntil: string | null;
  fixedPrice: ReservationMoney | null;
  productId: string;
  productTitle: string;
  productSlug: string;
  productImage: string | null;
};

/** `SellerReservation` from the contract. Nothing about the buyer. */
export type SellerReservation = {
  id: string;
  status: ReservationStatus;
  requestExpiresAt: string;
  reservedUntil: string | null;
  productId: string;
  productTitle: string;
  productSlug: string;
  productImage: string | null;
};

export type ReservationEligibility = {
  allowed: boolean;
  reason: ReservationIneligibleReason | null;
  answerBy: string | null;
};

/** The reservation fields of one product, as the viewer may see them. */
export type ProductReservationState = {
  /** Public, 'YYYY-MM-DD' (Europe/Amsterdam). */
  reservedUntilDate: string | null;
  myReservation: BuyerReservation | null;
  eligibility: ReservationEligibility | null;
};

export const EMPTY_PRODUCT_RESERVATION: ProductReservationState = {
  reservedUntilDate: null,
  myReservation: null,
  eligibility: null,
};

export type ReservationMutationResult =
  | {
      ok: true;
      /** The row as the gateway returned it after the transition, when parseable. */
      reservation?: SellerReservation | BuyerReservation | null;
    }
  | { ok: false; code: string | null };

/* -- transport ------------------------------------------------------------- */

type GraphQlError = { message?: string; extensions?: { code?: string } | null };

type RequestResult =
  | { ok: true; data: Record<string, unknown> }
  | { ok: false; code: string | null };

const TIMEOUT_MS = 10_000;

/** One operation; the token is optional because `reservedUntilDate` is public. */
export const reservationRequest = async (
  operation: string,
  variables: Record<string, unknown>,
  token: string | null
): Promise<RequestResult> => {
  const controller =
    typeof AbortController === "undefined" ? null : new AbortController();
  const timer = controller
    ? setTimeout(() => controller.abort(), TIMEOUT_MS)
    : null;

  try {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(GRAPHQL_ENDPOINT, {
      method: "POST",
      headers,
      body: JSON.stringify({ query: operation, variables }),
      signal: controller?.signal,
    });

    if (!response.ok) {
      return { ok: false, code: null };
    }

    const payload = (await response.json()) as {
      data?: Record<string, unknown> | null;
      errors?: GraphQlError[] | null;
    };
    const firstError = payload.errors?.[0];

    if (firstError) {
      return { ok: false, code: firstError.extensions?.code ?? null };
    }

    if (!payload.data) {
      return { ok: false, code: null };
    }

    return { ok: true, data: payload.data };
  } catch {
    return { ok: false, code: null };
  } finally {
    if (timer) {
      clearTimeout(timer);
    }
  }
};

/* -- parsing: untrusted until it has the contract's shape ------------------ */

const STATUSES: readonly ReservationStatus[] = [
  "REQUESTED",
  "ACCEPTED",
  "DECLINED",
  "REQUEST_EXPIRED",
  "EXPIRED",
  "RELEASED",
  "LIFTED",
  "COMPLETED",
];

const REASONS: readonly ReservationIneligibleReason[] = [
  "NOT_LOGGED_IN",
  "NOT_PRO_BUYER",
  "OWN_LISTING",
  "NOT_RESERVABLE",
  "RESERVED",
  "ALREADY_REQUESTED",
  "LIMIT_REACHED",
  "COOLDOWN",
  "FEATURE_DISABLED",
];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const str = (value: unknown) => (typeof value === "string" ? value : null);

const parseMoney = (value: unknown): ReservationMoney | null => {
  if (!isRecord(value) || typeof value.amount !== "number") {
    return null;
  }

  const currency = str(value.currency);

  return currency ? { amount: value.amount, currency } : null;
};

const parseStatus = (value: unknown): ReservationStatus | null =>
  STATUSES.find(status => status === value) ?? null;

export const parseSellerReservation = (
  value: unknown
): SellerReservation | null => {
  if (!isRecord(value)) {
    return null;
  }

  const id = str(value.id);
  const status = parseStatus(value.status);
  const requestExpiresAt = str(value.requestExpiresAt);
  const productId = str(value.productId);

  if (!id || !status || !requestExpiresAt || !productId) {
    return null;
  }

  return {
    id,
    status,
    requestExpiresAt,
    reservedUntil: str(value.reservedUntil),
    productId,
    productTitle: str(value.productTitle) ?? "",
    productSlug: str(value.productSlug) ?? "",
    productImage: str(value.productImage),
  };
};

export const parseBuyerReservation = (
  value: unknown
): BuyerReservation | null => {
  const base = parseSellerReservation(value);

  if (!base || !isRecord(value)) {
    return null;
  }

  return { ...base, fixedPrice: parseMoney(value.fixedPrice) };
};

export const parseEligibility = (
  value: unknown
): ReservationEligibility | null => {
  if (!isRecord(value) || typeof value.allowed !== "boolean") {
    return null;
  }

  return {
    allowed: value.allowed,
    reason: REASONS.find(reason => reason === value.reason) ?? null,
    answerBy: str(value.answerBy),
  };
};

const parseList = <T>(value: unknown, parse: (item: unknown) => T | null) =>
  Array.isArray(value)
    ? value.map(parse).filter((item): item is T => item !== null)
    : [];

/* -- operations ------------------------------------------------------------ */

const RESERVATION_FIELDS =
  "id status requestExpiresAt reservedUntil productId productTitle productSlug productImage";

const BUYER_RESERVATION_SELECTION = `{ ${RESERVATION_FIELDS} fixedPrice { amount currency } }`;

const SELLER_RESERVATION_SELECTION = `{ ${RESERVATION_FIELDS} }`;

export const PRODUCT_RESERVATION_PUBLIC_OPERATION =
  "query ReservationProductPublic($id: UUID!) {" +
  " product(id: $id) { id reservedUntilDate } }";

export const PRODUCT_RESERVATION_VIEWER_OPERATION =
  "query ReservationProductViewer($id: UUID!) {" +
  " product(id: $id) { id reservedUntilDate" +
  ` myReservation ${BUYER_RESERVATION_SELECTION}` +
  " reservationEligibility { allowed reason answerBy } } }";

export const MY_RESERVATIONS_OPERATION = `query MyReservations { myReservations ${BUYER_RESERVATION_SELECTION} }`;

export const SELLER_RESERVATIONS_OPERATION = `query SellerReservations { sellerReservations ${SELLER_RESERVATION_SELECTION} }`;

export const REQUEST_RESERVATION_OPERATION =
  "mutation RequestReservation($productId: UUID!) {" +
  ` requestReservation(productId: $productId) ${BUYER_RESERVATION_SELECTION} }`;

export const RELEASE_RESERVATION_OPERATION =
  "mutation ReleaseReservation($id: UUID!) {" +
  ` releaseReservation(id: $id) ${BUYER_RESERVATION_SELECTION} }`;

export const ACCEPT_RESERVATION_OPERATION =
  "mutation AcceptReservation($id: UUID!) {" +
  ` acceptReservation(id: $id) ${SELLER_RESERVATION_SELECTION} }`;

export const DECLINE_RESERVATION_OPERATION =
  "mutation DeclineReservation($id: UUID!) {" +
  ` declineReservation(id: $id) ${SELLER_RESERVATION_SELECTION} }`;

/* -- readers (fail-soft) --------------------------------------------------- */

export const fetchProductReservation = async (
  token: string | null,
  productId: string
): Promise<ProductReservationState> => {
  const result = await reservationRequest(
    token
      ? PRODUCT_RESERVATION_VIEWER_OPERATION
      : PRODUCT_RESERVATION_PUBLIC_OPERATION,
    { id: productId },
    token
  );

  if (!result.ok || !isRecord(result.data.product)) {
    return EMPTY_PRODUCT_RESERVATION;
  }

  const product = result.data.product;

  return {
    reservedUntilDate: str(product.reservedUntilDate),
    myReservation: parseBuyerReservation(product.myReservation),
    eligibility: parseEligibility(product.reservationEligibility),
  };
};

export const fetchMyReservations = async (
  token: string | null
): Promise<BuyerReservation[]> => {
  if (!token) {
    return [];
  }

  const result = await reservationRequest(MY_RESERVATIONS_OPERATION, {}, token);

  return result.ok
    ? parseList(result.data.myReservations, parseBuyerReservation)
    : [];
};

export const fetchSellerReservations = async (
  token: string | null
): Promise<SellerReservation[]> => {
  if (!token) {
    return [];
  }

  const result = await reservationRequest(
    SELLER_RESERVATIONS_OPERATION,
    {},
    token
  );

  return result.ok
    ? parseList(result.data.sellerReservations, parseSellerReservation)
    : [];
};

/* -- mutations ------------------------------------------------------------- */

const mutate = async (
  operation: string,
  field: string,
  variables: Record<string, unknown>,
  token: string | null
): Promise<ReservationMutationResult> => {
  if (!token) {
    return { ok: false, code: "NOT_LOGGED_IN" };
  }

  const result = await reservationRequest(operation, variables, token);

  if (!result.ok) {
    return { ok: false, code: result.code };
  }

  return { ok: true, reservation: parseBuyerReservation(result.data[field]) };
};

export const requestReservation = (token: string | null, productId: string) =>
  mutate(
    REQUEST_RESERVATION_OPERATION,
    "requestReservation",
    { productId },
    token
  );

export const releaseReservation = (token: string | null, id: string) =>
  mutate(RELEASE_RESERVATION_OPERATION, "releaseReservation", { id }, token);

export const acceptReservation = (token: string | null, id: string) =>
  mutate(ACCEPT_RESERVATION_OPERATION, "acceptReservation", { id }, token);

export const declineReservation = (token: string | null, id: string) =>
  mutate(DECLINE_RESERVATION_OPERATION, "declineReservation", { id }, token);
